import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  Mail, Phone, Key, Shield, Copy, Eye, EyeOff,
  RefreshCw, CheckCircle2, Building2, Percent, Info, Save, AlertTriangle,
} from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { useQuery } from "@tanstack/react-query";
import * as resellerService from "@/shared/api/services/resellerService";

/* ── Section card ── */
function SectionCard({ title, icon: Icon, accentColor = "#6366f1", delay = 0, children }) {
  const [h, setH] = useState(false);
  return (
    <motion.div
      initial={{ opacity:0, y:22 }}
      animate={{ opacity:1, y:0 }}
      transition={{ delay, duration:.45, ease:[0.34,1.56,0.64,1] }}
      onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}
      style={{
        background:"linear-gradient(135deg,rgba(13,27,46,.9) 0%,rgba(15,30,55,.9) 100%)",
        border:`1px solid ${h?accentColor+"38":accentColor+"18"}`,
        borderRadius:"20px",
        overflow:"hidden",
        transition:"all .42s cubic-bezier(0.34,1.56,0.64,1)",
        boxShadow: h ? `0 16px 44px ${accentColor}18, 0 0 0 1px ${accentColor}20` : "0 4px 16px rgba(0,0,0,.25)",
      }}
    >
      {/* animated top bar */}
      <div style={{ height:"2px", background: h ? `linear-gradient(90deg,transparent,${accentColor}90,${accentColor}70,transparent)` : `linear-gradient(90deg,transparent,${accentColor}40,transparent)`, transition:"all .42s ease" }} />
      {/* header */}
      <div style={{ display:"flex", alignItems:"center", gap:"12px", padding:"18px 22px", borderBottom:"1px solid rgba(56,189,248,.06)" }}>
        <div style={{ width:"38px", height:"38px", borderRadius:"11px", background:`${accentColor}18`, border:`1px solid ${accentColor}30`, display:"flex", alignItems:"center", justifyContent:"center", transition:"all .4s cubic-bezier(0.34,1.56,0.64,1)", transform: h?"rotate(-6deg) scale(1.12)":"none", boxShadow: h?`0 4px 14px ${accentColor}30`:"none" }}>
          <Icon style={{ width:"17px", height:"17px", color:accentColor }} />
        </div>
        <h2 style={{ fontSize:"15px", fontWeight:700, color: h?"#f0f9ff":"#e2e8f0", margin:0, transition:"color .2s" }}>{title}</h2>
      </div>
      <div style={{ padding:"22px" }}>{children}</div>
    </motion.div>
  );
}

/* ── Dark input ── */
function DInput({ type="text", value, onChange, placeholder, disabled, icon:Icon }) {
  const [f, setF] = useState(false);
  return (
    <div style={{ position:"relative" }}>
      {Icon && <Icon style={{ position:"absolute", left:"13px", top:"50%", transform:"translateY(-50%)", width:"15px", height:"15px", color: f?"#6366f1":"#64748b", transition:"color .2s", pointerEvents:"none" }} />}
      <input
        type={type} value={value} onChange={onChange} placeholder={placeholder} disabled={disabled}
        onFocus={()=>setF(true)} onBlur={()=>setF(false)}
        style={{
          width:"100%", padding:`10px 13px 10px ${Icon?"40px":"13px"}`,
          borderRadius:"11px",
          background: disabled?"rgba(15,23,42,.5)":"rgba(13,27,46,.85)",
          border:`1px solid ${f?"rgba(99,102,241,.5)":disabled?"rgba(148,163,184,.1)":"rgba(99,102,241,.18)"}`,
          color: disabled?"#475569":"#e2e8f0",
          fontSize:"13px", outline:"none",
          transition:"all .28s ease",
          boxShadow: f?"0 0 0 3px rgba(99,102,241,.1)":"none",
          cursor:disabled?"not-allowed":"text",
          boxSizing:"border-box",
        }}
      />
    </div>
  );
}

/* ── Field label ── */
function Field({ label, children }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
      <label style={{ fontSize:"11px", fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.08em" }}>{label}</label>
      {children}
    </div>
  );
}

/* ── Key row with show/copy ── */
function KeyRow({ label, value, show, onToggleShow, onCopy, copied, color="#38bdf8" }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"6px" }}>
      <label style={{ fontSize:"11px", fontWeight:700, color:"#64748b", textTransform:"uppercase", letterSpacing:"0.08em" }}>{label}</label>
      <div style={{ display:"flex", gap:"8px" }}>
        <div style={{ flex:1, padding:"11px 14px", background:"rgba(8,14,26,.95)", borderRadius:"11px", fontFamily:"monospace", fontSize:"11px", color, overflowX:"auto", border:`1px solid ${color}22`, letterSpacing:"0.04em", whiteSpace:"nowrap" }}>
          {show ? value : "••••••••••••••••••••••••••••••••••••"}
        </div>
        {[
          { onClick:onToggleShow, icon: show?EyeOff:Eye,       title:"Toggle visibility", accent:color },
          { onClick:onCopy,       icon: copied?CheckCircle2:Copy, title:"Copy",            accent:"#10b981" },
        ].map(({ onClick, icon:Icon, title, accent })=>(
          <button key={title} onClick={onClick} title={title}
            style={{ width:"40px", height:"40px", borderRadius:"11px", background:`${accent}0d`, border:`1px solid ${accent}28`, color:accent, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, transition:"all .36s cubic-bezier(0.34,1.56,0.64,1)" }}
            onMouseEnter={e=>{ e.currentTarget.style.background=`${accent}22`; e.currentTarget.style.transform="translateY(-3px) scale(1.12)"; e.currentTarget.style.boxShadow=`0 6px 18px ${accent}30`; }}
            onMouseLeave={e=>{ e.currentTarget.style.background=`${accent}0d`; e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="none"; }}
          >
            <Icon style={{ width:"15px", height:"15px" }} />
          </button>
        ))}
      </div>
    </div>
  );
}

/* ── Commission metric ── */
function CommMetric({ label, value, color }) {
  return (
    <div style={{ background:"rgba(8,14,26,.6)", borderRadius:"12px", padding:"14px 16px", border:`1px solid ${color}18` }}>
      <p style={{ fontSize:"10px", color:color, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", margin:"0 0 6px" }}>{label}</p>
      <p style={{ fontSize:"20px", fontWeight:800, color:"#f0f9ff", margin:0 }}>{value}</p>
    </div>
  );
}

export default function ResellerSettings() {
  const { t } = useLanguage();
  const resellerName = "Asia Gaming Network";

  // ── exact original query ──
  const { data: resellerProfile } = useQuery({
    queryKey: ["reseller-profile"],
    queryFn: () => resellerService.getProfile(),
  });

  // ── exact original state ──
  const [reseller, setReseller] = useState({
    name: "Asia Gaming Network",
    code: "RSL002",
    email: "admin@asiagaming.net",
    support_email: "support@asiagaming.net",
    contact_phone: "+66 91 234 5678",
    api_key: "rsl_pk_live_q2w3e4r5t6y7u8i9o0p1a2s3d4f5g6h7",
    secret_key: "rsl_sk_live_m9n8b7v6c5x4z3a2s1d0f9g8h7j6k5l4",
  });

  const [showApiKey, setShowApiKey]     = useState(false);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [regenerateDialog, setRegenDialog] = useState(false);
  const [copied, setCopied]             = useState(null);
  const [saving, setSaving]             = useState(false);

  // ── exact original handlers ──
  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopied(type);
    setTimeout(()=>setCopied(null), 2000);
  };

  const handleRegenerate = () => {
    const newKey    = "rsl_pk_live_" + Array.from({length:32},()=>"abcdefghijklmnopqrstuvwxyz0123456789"[Math.floor(Math.random()*36)]).join("");
    const newSecret = "rsl_sk_live_" + Array.from({length:32},()=>"abcdefghijklmnopqrstuvwxyz0123456789"[Math.floor(Math.random()*36)]).join("");
    setReseller(prev=>({ ...prev, api_key:newKey, secret_key:newSecret }));
    setRegenDialog(false);
  };

  const handleSave = async () => {
    setSaving(true);
    await new Promise(r=>setTimeout(r,1000));
    setSaving(false);
  };

  return (
    <ResellerLayout currentPage="ResellerSettings" resellerName={resellerName}>
      <style>{`@keyframes settingsSpin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ maxWidth:"760px" }}>

        {/* Header */}
        <motion.div initial={{ opacity:0, y:-12 }} animate={{ opacity:1, y:0 }} style={{ marginBottom:"28px" }}>
          <div style={{ display:"flex", alignItems:"center", gap:"14px" }}>
            <div style={{ width:"46px", height:"46px", borderRadius:"14px", background:"linear-gradient(135deg,#6366f1,#8b5cf6)", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 4px 20px rgba(99,102,241,.5)", flexShrink:0 }}>
              <Building2 style={{ width:"21px", height:"21px", color:"#fff" }} />
            </div>
            <div>
              <h1 style={{ fontSize:"24px", fontWeight:800, color:"#f0f9ff", letterSpacing:"-0.025em", margin:0 }}>{t("settings")||"Settings"}</h1>
              <p style={{ fontSize:"13px", color:"#475569", margin:0 }}>{t("manageProfile")||"Manage your reseller profile and API credentials"}</p>
            </div>
          </div>
        </motion.div>

        <div style={{ display:"flex", flexDirection:"column", gap:"20px" }}>

          {/* ── Profile Card ── */}
          <SectionCard title={t("resellerProfile")||"Reseller Profile"} icon={Building2} accentColor="#6366f1" delay={0}>
            <div style={{ display:"flex", flexDirection:"column", gap:"16px" }}>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"14px" }}>
                <Field label={t("businessName")||"Business Name"}>
                  <DInput value={reseller.name} onChange={e=>setReseller(p=>({...p,name:e.target.value}))} />
                </Field>
                <Field label={t("code")||"Code"}>
                  <DInput value={reseller.code} disabled />
                </Field>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"14px" }}>
                <Field label={t("email")||"Email"}>
                  <DInput type="email" icon={Mail} value={reseller.email} onChange={e=>setReseller(p=>({...p,email:e.target.value}))} placeholder="admin@company.com" />
                </Field>
                <Field label={t("supportEmail")||"Support Email"}>
                  <DInput type="email" icon={Mail} value={reseller.support_email} onChange={e=>setReseller(p=>({...p,support_email:e.target.value}))} placeholder="support@company.com" />
                </Field>
              </div>
              <Field label={t("phone")||"Phone"}>
                <DInput icon={Phone} value={reseller.contact_phone} onChange={e=>setReseller(p=>({...p,contact_phone:e.target.value}))} />
              </Field>

              {/* Save button */}
              <div style={{ paddingTop:"6px" }}>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  style={{
                    display:"flex", alignItems:"center", gap:"8px",
                    padding:"11px 22px", borderRadius:"12px",
                    background:"linear-gradient(135deg,#6366f1,#8b5cf6)",
                    border:"none", color:"#fff", fontSize:"13px", fontWeight:700,
                    cursor: saving?"not-allowed":"pointer",
                    transition:"all .42s cubic-bezier(0.34,1.56,0.64,1)",
                    boxShadow:"0 4px 18px rgba(99,102,241,.45)",
                    opacity: saving?.6:1,
                  }}
                  onMouseEnter={e=>{ if(!saving){ e.currentTarget.style.transform="translateY(-3px) scale(1.04)"; e.currentTarget.style.boxShadow="0 10px 28px rgba(99,102,241,.6)"; } }}
                  onMouseLeave={e=>{ e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="0 4px 18px rgba(99,102,241,.45)"; }}
                >
                  {saving
                    ? <><RefreshCw style={{ width:"14px", height:"14px", animation:"settingsSpin 1s linear infinite" }} />{t("saving")||"Saving…"}</>
                    : <><Save style={{ width:"14px", height:"14px" }} />{t("saveChanges")||"Save Changes"}</>
                  }
                </button>
              </div>
            </div>
          </SectionCard>

          {/* ── API Credentials Card ── */}
          <SectionCard title={t("apiCredentialsTitle")||"API Credentials"} icon={Key} accentColor="#38bdf8" delay={0.08}>
            <div style={{ display:"flex", flexDirection:"column", gap:"18px" }}>
              {/* Security banner */}
              <div style={{ display:"flex", gap:"10px", padding:"13px 15px", background:"rgba(245,158,11,.08)", border:"1px solid rgba(245,158,11,.22)", borderRadius:"12px" }}>
                <Shield style={{ width:"16px", height:"16px", color:"#f59e0b", flexShrink:0 }} />
                <p style={{ margin:0, fontSize:"12px", color:"#fbbf24" }}>{t("keepSecretConfidential")||"Keep your secret key confidential. Never expose it in client-side code."}</p>
              </div>

              <KeyRow
                label={t("apiKey")||"API Key"}
                value={reseller.api_key}
                show={showApiKey}
                onToggleShow={()=>setShowApiKey(!showApiKey)}
                onCopy={()=>copyToClipboard(reseller.api_key,"api")}
                copied={copied==="api"}
                color="#38bdf8"
              />
              <KeyRow
                label={t("secretKey")||"Secret Key"}
                value={reseller.secret_key}
                show={showSecretKey}
                onToggleShow={()=>setShowSecretKey(!showSecretKey)}
                onCopy={()=>copyToClipboard(reseller.secret_key,"secret")}
                copied={copied==="secret"}
                color="#c084fc"
              />
            </div>
          </SectionCard>

          {/* ── Fees & Commission Card ── */}
          {resellerProfile && (
            <SectionCard title={t("feesAndCommission")||"Fees & Commission"} icon={Percent} accentColor="#10b981" delay={0.16}>
              <div style={{ display:"flex", flexDirection:"column", gap:"18px" }}>
                {/* Parent commission info */}
                {resellerProfile.parentName && (
                  <div style={{ padding:"16px", background:"rgba(56,189,248,.07)", border:"1px solid rgba(56,189,248,.18)", borderRadius:"14px" }}>
                    <div style={{ display:"flex", gap:"10px" }}>
                      <Info style={{ width:"16px", height:"16px", color:"#38bdf8", flexShrink:0, marginTop:"2px" }} />
                      <div>
                        <p style={{ fontSize:"13px", fontWeight:600, color:"#7dd3fc", margin:"0 0 12px" }}>
                          {(t("givenBy")||"Given by {type} {name}").replace("{type}",resellerProfile.parentType).replace("{name}",resellerProfile.parentName)}
                        </p>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"10px" }}>
                          <CommMetric label={t("depositCommission")||"Deposit"} value={resellerProfile.parentDepositCommission!=null?`${resellerProfile.parentDepositCommission}%`:(t("nA")||"N/A")} color="#38bdf8" />
                          <CommMetric label={t("withdrawalCommission")||"Withdrawal"} value={resellerProfile.parentWithdrawalCommission!=null?`${resellerProfile.parentWithdrawalCommission}%`:(t("nA")||"N/A")} color="#38bdf8" />
                          <CommMetric label={t("percentageFee")||"Pct Fee"} value={resellerProfile.parentPercentageFee!=null?`${resellerProfile.parentPercentageFee}%`:(t("nA")||"N/A")} color="#38bdf8" />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Your rates */}
                <div>
                  <p style={{ fontSize:"12px", color:"#64748b", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:"12px" }}>{t("yourCommissionRates")||"Your Commission Rates"}</p>
                  <p style={{ fontSize:"12px", color:"#475569", marginBottom:"14px", marginTop:"-8px" }}>{t("yourCommissionRatesDesc")||"These rates are set by your parent and cannot be changed."}</p>
                  <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"12px" }}>
                    <CommMetric label={t("depositCommissionPercent")||"Deposit %"} value={resellerProfile.depositCommission!=null?resellerProfile.depositCommission:(t("nA")||"N/A")} color="#10b981" />
                    <CommMetric label={t("withdrawalCommissionPercent")||"Withdrawal %"} value={resellerProfile.withdrawalCommission!=null?resellerProfile.withdrawalCommission:(t("nA")||"N/A")} color="#10b981" />
                    <CommMetric label={t("percentageFeePercent")||"Pct Fee %"} value={resellerProfile.percentageFee!=null?resellerProfile.percentageFee:(t("nA")||"N/A")} color="#10b981" />
                  </div>
                </div>
              </div>
            </SectionCard>
          )}

          {/* ── Danger Zone ── */}
          <SectionCard title={t("regenerateApiKeysTitle")||"Regenerate API Keys"} icon={AlertTriangle} accentColor="#ef4444" delay={0.24}>
            <div>
              <p style={{ fontSize:"13px", color:"#64748b", marginBottom:"16px" }}>
                {t("invalidateImmediately")||"Regenerating keys will immediately invalidate current keys. All integrations using the old keys will stop working."}
              </p>
              <button
                onClick={()=>setRegenDialog(true)}
                style={{ display:"flex", alignItems:"center", gap:"8px", padding:"11px 20px", borderRadius:"12px", background:"rgba(239,68,68,.1)", border:"1px solid rgba(239,68,68,.3)", color:"#f87171", fontSize:"13px", fontWeight:700, cursor:"pointer", transition:"all .4s cubic-bezier(0.34,1.56,0.64,1)" }}
                onMouseEnter={e=>{ e.currentTarget.style.background="rgba(239,68,68,.2)"; e.currentTarget.style.transform="translateY(-3px) scale(1.04)"; e.currentTarget.style.boxShadow="0 8px 24px rgba(239,68,68,.25)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.background="rgba(239,68,68,.1)"; e.currentTarget.style.transform="none"; e.currentTarget.style.boxShadow="none"; }}
              >
                <RefreshCw style={{ width:"15px", height:"15px" }} />
                {t("regenerateAllKeys")||"Regenerate All Keys"}
              </button>
            </div>
          </SectionCard>
        </div>
      </div>

      {/* ── Alert Dialog ── */}
      <AlertDialog open={regenerateDialog} onOpenChange={setRegenDialog}>
        <AlertDialogContent style={{ background:"#0d1b2e", border:"1px solid rgba(239,68,68,.25)", borderRadius:"18px" }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color:"#f0f9ff", display:"flex", alignItems:"center", gap:"8px" }}>
              <AlertTriangle style={{ width:"18px", height:"18px", color:"#ef4444" }} />
              {t("regenerateApiKeys")||"Regenerate API Keys"}
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color:"#64748b" }}>
              {t("invalidateImmediately")||"This will immediately invalidate all existing API keys. Any integrations using current keys will stop working."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ background:"rgba(148,163,184,.1)", border:"1px solid rgba(148,163,184,.2)", color:"#94a3b8", borderRadius:"10px" }}>
              {t("cancel")||"Cancel"}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleRegenerate} style={{ background:"#dc2626", border:"none", borderRadius:"10px" }}>
              {t("regenerate")||"Regenerate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ResellerLayout>
  );
}